import { apiErrorStatus, RequestError } from '@/domain/shared/errors';

interface SentryFrame {
    filename?: string | null;
    abs_path?: string | null;
    function?: string | null;
    module?: string | null;
}

export interface SentryEventLike {
    exception?: {
        values?: Array<{
            type?: string;
            value?: unknown;
            stacktrace?: { frames?: SentryFrame[] };
        }>;
    };
    breadcrumbs?: Array<{
        category?: string;
        data?: { method?: string; url?: string; status_code?: number };
    }>;
}

/** The part of Sentry's `EventHint` the filters read. */
export interface SentryHintLike {
    originalException?: unknown;
}

const HARVEST_REPORT_PATH = /^\/api\/v2\/reports\/\d+\/\d+$/;

function isJsonObject(value: unknown): value is Record<string, unknown> {
    return value != null && typeof value === 'object' && !Array.isArray(value);
}

function pathFromUrl(url: string): string {
    try {
        return new URL(url, 'https://web.optoscale.no').pathname;
    } catch {
        return url;
    }
}

/**
 * Harvest GET detail-read 404s are expected (report not generated yet). The failing request
 * on the API client's error decides: DELETE 404s on the same path are mutation failures and
 * stay in Sentry, as does anything whose method or URL is missing.
 */
function isExpectedHarvestRequestError(error: RequestError): boolean {
    const config = error.response?.config;
    return (
        apiErrorStatus(error) === 404 &&
        config?.method?.toUpperCase() === 'GET' &&
        typeof config.url === 'string' &&
        HARVEST_REPORT_PATH.test(pathFromUrl(config.url))
    );
}

/**
 * The harvest path of a Nest 404 body, which the event carries as an object (what the API
 * client puts on `message`) or as its JSON text.
 */
function harvestNotFoundPath(value: unknown): string | undefined {
    let body = value;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed.startsWith('{')) {
            return undefined;
        }
        try {
            body = JSON.parse(trimmed);
        } catch {
            return undefined;
        }
    }
    if (!isJsonObject(body) || body.statusCode !== 404 || typeof body.path !== 'string') {
        return undefined;
    }
    const path = pathFromUrl(body.path);
    return HARVEST_REPORT_PATH.test(path) ? path : undefined;
}

/**
 * Whether the latest request to `path` was a GET that did not answer with another status.
 * Only the latest one counts: an older GET says nothing about the request that failed.
 */
function latestRequestWasGet404(event: SentryEventLike, path: string): boolean {
    const crumbs = event.breadcrumbs ?? [];
    for (let index = crumbs.length - 1; index >= 0; index -= 1) {
        const data = crumbs[index]?.data;
        if (typeof data?.url !== 'string' || pathFromUrl(data.url) !== path) {
            continue;
        }
        return (
            typeof data.method === 'string' &&
            data.method.toUpperCase() === 'GET' &&
            (data.status_code == null || data.status_code === 404)
        );
    }
    return false;
}

function stackMentionsHarvestDelete(event: SentryEventLike): boolean {
    const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
    return frames.some((frame) => (frame.function ?? '').includes('DeleteReport'));
}

/**
 * The fallback for an event without the API client's error: the latest breadcrumb for the
 * same report must be a GET. If it cannot be confirmed as GET, keep the event.
 */
export function isExpectedClientErrorMessage(
    message: unknown,
    event: SentryEventLike = {}
): boolean {
    const path = harvestNotFoundPath(message);
    if (path === undefined || stackMentionsHarvestDelete(event)) {
        return false;
    }
    return latestRequestWasGet404(event, path);
}

function isChartJsFrame(frame: SentryFrame): boolean {
    const path = `${frame.filename ?? ''} ${frame.abs_path ?? ''} ${frame.module ?? ''}`;
    return path.includes('chart.js') || path.includes('helpers.segment');
}

export function isDetachedChartCanvasError(event: SentryEventLike): boolean {
    const exception = event.exception?.values?.[0];
    if (!exception) {
        return false;
    }
    const value = typeof exception.value === 'string' ? exception.value : '';
    const mentionsParentNode =
        value.includes("reading 'parentNode'") || value.includes('reading "parentNode"');
    const frames = exception.stacktrace?.frames ?? [];
    return mentionsParentNode && frames.some(isChartJsFrame);
}

export function shouldDropSentryEvent(
    event: SentryEventLike,
    originalException?: unknown
): boolean {
    const isExpectedClientError = RequestError.isRequestError(originalException)
        ? isExpectedHarvestRequestError(originalException)
        : isExpectedClientErrorMessage(event.exception?.values?.[0]?.value, event);
    return isExpectedClientError || isDetachedChartCanvasError(event);
}

/** Sentry's `beforeSend`: drops expected noise and sends everything else unchanged. */
export function filterSentryEvent<TEvent extends SentryEventLike>(
    event: TEvent,
    hint?: SentryHintLike
): TEvent | null {
    try {
        return shouldDropSentryEvent(event, hint?.originalException) ? null : event;
    } catch (error) {
        // Rethrowing or capturing here would make Sentry replace this event with the error,
        // so only warn.
        console.warn('Sentry event filter failed; sending the event unfiltered', error);
        return event;
    }
}
